import { BASE_URL } from "#next-seo.config";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import BlogPost from "~/components/BlogPost";
import Container from "~/components/Container";
import InteractiveBadge from "~/components/InteractiveBadge";
import Subscribe from "~/components/Subscribe";
import { apiUrl } from "~/config";
import { blogIndexHref, INTERACTIVE, pageCount, POSTS_PER_PAGE } from "~/lib/blog";
import type { BlogIndexProps } from "~/lib/blog-index";
import fetcher from "~/lib/fetcher";

// Links and buttons share these, so paging looks the same whether it navigates or searches. The `!` background
// classes are for the buttons, which global.scss paints gray (see "Every <button> starts gray" in AGENTS.md).
const pageControl =
  "inline-block rounded px-3 py-1 text-base no-underline shadow-none text-gray-700 dark:text-gray-300 !bg-transparent hover:!bg-gray-100 dark:!bg-transparent dark:hover:!bg-gray-800";
const currentPage =
  "inline-block rounded px-3 py-1 text-base bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900";

function Pagination({
  page,
  totalPages,
  href,
  onPage
}: {
  page: number;
  totalPages: number;
  /** Each page's URL, when every page is a page of its own. */
  href?: (page: number) => string;
  /** Otherwise, called with the page to show. */
  onPage?: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return null;
  }

  const control = (to: number, label: string, ariaLabel = `Page ${to}`) =>
    href ? (
      <Link href={href(to)} aria-label={ariaLabel} className={pageControl}>
        {label}
      </Link>
    ) : (
      <button type="button" aria-label={ariaLabel} onClick={() => onPage(to)} className={pageControl}>
        {label}
      </button>
    );

  return (
    <nav aria-label="Pagination" className="flex items-center justify-between w-full mt-4">
      <div className="w-28">{page > 1 && control(page - 1, "← Newer", "Newer posts")}</div>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) =>
          n === page ? (
            <span key={n} aria-current="page" className={currentPage}>
              {n}
            </span>
          ) : (
            <Fragment key={n}>{control(n, String(n))}</Fragment>
          )
        )}
      </div>
      <div className="w-28 text-right">{page < totalPages && control(page + 1, "Older →", "Older posts")}</div>
    </nav>
  );
}

type Sort = "newest" | "oldest" | "popular" | "unpopular";

const SORTS: { value: Sort; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "popular", label: "Most popular" },
  { value: "unpopular", label: "Least popular" }
];

/**
 * An icon button that opens a small menu of orderings. The button and rows are at least 40px tall so a thumb
 * can hit them, and the menu is anchored to the button's right edge so it never runs off a narrow screen.
 */
function SortMenu({ sort, onSort }: { sort: Sort; onSort: (sort: Sort) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const label = SORTS.find(({ value }) => value === sort).label;

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e.type === "keydown" ? (e as KeyboardEvent).key === "Escape" : !root.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        aria-label={`Sort posts, now: ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1 text-sm leading-5 text-gray-700 shadow-none !bg-transparent hover:!bg-transparent hover:border-blue-500 dark:border-gray-700 dark:text-gray-300 dark:!bg-transparent dark:hover:!bg-transparent dark:hover:border-blue-500"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        >
          <path d="M7 4v16m0 0l-3-3m3 3l3-3" />
          <path d="M17 20V4m0 0l-3 3m3-3l3 3" />
        </svg>
        {label}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Sort posts"
          className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          {SORTS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={value === sort}
              onClick={() => {
                onSort(value);
                setOpen(false);
              }}
              className="flex min-h-10 w-full items-center justify-between rounded-none px-4 text-left text-base text-gray-800 shadow-none !bg-transparent hover:!bg-gray-100 dark:text-gray-200 dark:!bg-transparent dark:hover:!bg-gray-700"
            >
              {label}
              {value === sort && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Listing({ posts, category, page, totalPosts, categories }: BlogIndexProps) {
  const [searchValue, setSearchValue] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const router = useRouter();
  // The order lives in the URL (`?sort=popular`), so a link can set it. The pages are static, so the query is
  // only known once the router is ready; until then the listing shows the default.
  const [sort, setSort] = useState<Sort>("newest");
  const querySort = router.query.sort;
  useEffect(() => {
    if (!router.isReady) return;
    setSort(SORTS.find(({ value }) => value === querySort)?.value ?? "newest");
    setSearchPage(1);
  }, [router.isReady, querySort]);
  const listTop = useRef<HTMLHeadingElement>(null);
  // Every post's views in one request, rather than one per post listed.
  const { data } = useSWR(`${apiUrl}/api/blog/views`, fetcher);
  const views: Record<string, number> | null = data?.views ?? null;

  const current = categories.find(({ slug }) => slug === category);
  const title = `${current ? `${current.name} – ` : ""}Samuel Batista's Blog${page > 1 ? ` – Page ${page}` : ""}`;
  const description = current
    ? current.description
    : "Samuel Batista's articles on how AI models work, setting up PCs, and whatever else is on his mind.";
  const url = `${BASE_URL}${blogIndexHref(category, page)}`;

  // Search and any order but the static one (newest first) look through the whole listing, not just this page,
  // and page it in place. Until the view counts arrive, or if they never do, the popularity orders wait.
  const byViews = (sort === "popular" || sort === "unpopular") && views !== null;
  const oldest = sort === "oldest";
  const searching = searchValue !== "";
  const inPlace = searching || byViews || oldest;
  const found = searching
    ? posts.filter(({ title }) => title.toLowerCase().includes(searchValue.toLowerCase()))
    : posts;
  const viewsOf = ({ slug }: { slug: string }) => views?.[`/blog/${slug}`] ?? 0;
  // Array.prototype.sort is stable, so equal counts keep their newest-first order.
  const matches = byViews
    ? [...found].sort((a, b) => (sort === "popular" ? viewsOf(b) - viewsOf(a) : viewsOf(a) - viewsOf(b)))
    : oldest
      ? [...found].reverse()
      : found;
  const shownPage = inPlace ? searchPage : page;
  const shown = matches.slice((shownPage - 1) * POSTS_PER_PAGE, shownPage * POSTS_PER_PAGE);

  return (
    <Container>
      <Head>
        {generateNextSeo({
          title,
          description,
          canonical: url,
          openGraph: {
            url,
            title,
            description
          }
        })}
      </Head>
      <div className="flex flex-col justify-center items-start max-w-2xl mx-auto mb-16">
        <h1 className="font-bold text-3xl md:text-5xl tracking-tight mb-4 text-black dark:text-white">Blog</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {`In total, I've written ${totalPosts} articles on this site.
            Search by title below, or pick a category.`}
        </p>
        <div className="relative w-full mb-4">
          <input
            aria-label={current ? `Search ${current.name} articles` : "Search articles"}
            type="text"
            autoFocus
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setSearchPage(1);
            }}
            placeholder={current ? `Search ${current.name} articles` : "Search articles"}
            className="px-4 py-2 border border-gray-300 dark:border-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <svg
            className="absolute right-3 top-3 h-5 w-5 text-gray-400 dark:text-gray-300"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <Subscribe />
        <div className="mb-4 mt-4 flex w-full items-center justify-between gap-4">
          <h3
            ref={listTop}
            className="scroll-mt-28 font-bold text-2xl md:text-4xl tracking-tight text-black dark:text-white"
          >
            {current ? current.name : "All Posts"}
          </h3>
          <SortMenu
            sort={sort}
            onSort={(to) => {
              setSort(to);
              setSearchPage(1);
              // Newest is the default, so it needs no parameter.
              router.replace(`${blogIndexHref(category, page)}${to === "newest" ? "" : `?sort=${to}`}`, undefined, {
                shallow: true,
                scroll: false
              });
            }}
          />
        </div>
        <nav aria-label="Categories" className="flex flex-wrap gap-2 mb-8 w-full">
          {[{ slug: null, name: "All", count: totalPosts }, ...categories].map(({ slug, name, count }) => (
            <Link
              key={name}
              href={blogIndexHref(slug)}
              aria-current={slug === category ? "true" : undefined}
              // The badge sits in the pill's round end, as far from its edge as from its top and bottom.
              className={`inline-flex flex-auto items-center justify-center rounded-full border ${slug === INTERACTIVE.slug ? "pl-1.5" : "pl-3"} pr-3 py-1 text-sm no-underline ${
                slug === category
                  ? "border-gray-900 bg-gray-900 text-white hover:text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900 dark:hover:text-gray-900"
                  : "border-gray-300 text-gray-700 hover:border-blue-500 dark:border-gray-700 dark:text-gray-300 dark:hover:border-blue-500"
              }`}
            >
              {slug === INTERACTIVE.slug && <InteractiveBadge decorative />}
              {name}
              <span className="ml-1.5 opacity-60">{count}</span>
            </Link>
          ))}
        </nav>
        {!shown.length && "No posts found."}
        {shown.map((frontMatter) => (
          <BlogPost
            key={`${frontMatter.isDraft ? "draft" : "post"}:${frontMatter.slug}`}
            post={frontMatter}
            views={views ? (views[`/blog/${frontMatter.slug}`] ?? 0) : null}
            fetchViews={false}
          />
        ))}
        {inPlace ? (
          <Pagination
            page={searchPage}
            totalPages={pageCount(matches.length)}
            onPage={(to) => {
              setSearchPage(to);
              listTop.current?.scrollIntoView();
            }}
          />
        ) : (
          <Pagination page={page} totalPages={pageCount(posts.length)} href={(to) => blogIndexHref(category, to)} />
        )}
      </div>
    </Container>
  );
}

export default function BlogIndex(props: BlogIndexProps) {
  // Next keeps a page's component mounted when navigating between two URLs of the same route, from one category
  // to another, say. Keying on the page makes every page start with a clear search, whichever route it is on.
  return <Listing key={blogIndexHref(props.category, props.page)} {...props} />;
}
